import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ordersApi, type CreateOrderSupportRequestDto, type OrderSupportRequest } from "@/lib/api";
import {
  rejectedAttachmentIndexes,
  uploadAttachmentsDirect,
  type AttachmentUploadProgress,
} from "@/lib/orderSupportRequestUpload";
import { supportRequestQueryKeys } from "./supportRequest";

/** Tipos ativos (`GET /orders/request-types`). Mudam só no banco, então podem ficar em cache por alguns minutos. */
export const useOrderRequestTypes = () =>
  useQuery({
    queryKey: supportRequestQueryKeys.types,
    queryFn: async () => {
      const types = await ordersApi.getRequestTypes();
      return Array.isArray(types) ? types : [];
    },
    staleTime: 5 * 60_000,
  });

/**
 * Solicitações do pedido (`GET /orders/{id}/requests`), da mais recente para a mais antiga. O back consulta o
 * Zendesk ticket a ticket para trazer o status ao vivo, então a busca só é ligada quando precisa (lista exibida,
 * ou menu aberto checando se a divergência já tem solicitação no pedido) e refaz a consulta a cada abertura (sem
 * `staleTime`).
 */
export const useOrderSupportRequests = (orderId: string, enabled: boolean) =>
  useQuery({
    queryKey: supportRequestQueryKeys.byOrder(orderId),
    queryFn: async () => {
      const requests = await ordersApi.getRequests(orderId);
      return Array.isArray(requests) ? requests : [];
    },
    enabled,
  });

/**
 * Anexos enviados na solicitação e respostas públicas do chamado (`GET /orders/{id}/requests/{ticketId}/comments`).
 * Busca só quando o acompanhamento é aberto — o back consulta o Zendesk a cada chamada — e refaz a cada abertura.
 * As seções de anexos e de respostas usam a mesma consulta (uma chamada só).
 */
export const useRequestComments = (orderId: string, zendeskTicketId: number) =>
  useQuery({
    queryKey: supportRequestQueryKeys.comments(orderId, zendeskTicketId),
    queryFn: async () => {
      const conversation = await ordersApi.getRequestComments(orderId, zendeskTicketId);
      return {
        requestAttachments: conversation?.requestAttachments ?? [],
        comments: conversation?.comments ?? [],
      };
    },
  });

/** O que o formulário envia: os arquivos escolhidos, que viram chaves do S3 antes da criação. */
export type CreateOrderSupportRequestInput = Omit<CreateOrderSupportRequestDto, "attachmentKeys"> & { attachments: File[] };

interface CreateOrderSupportRequestOptions {
  /** Chave de cada arquivo já enviado ao S3 nesta sessão do formulário: "Tentar novamente" não sobe de novo. */
  uploadedKeys?: Map<File, string>;
  /** Progresso dos anexos; `null` quando eles terminam e a solicitação está sendo criada. */
  onUploadProgress?: (progress: AttachmentUploadProgress | null) => void;
}

/**
 * Anexos direto ao S3 e depois `POST /orders/{id}/requests` só com as chaves. Depois de qualquer desfecho a lista do
 * pedido fica desatualizada (201 cria, 409 aponta para uma existente), então ela é invalidada e volta a ser buscada
 * na próxima exibição.
 */
export const useCreateOrderSupportRequest = (
  orderId: string,
  { uploadedKeys, onUploadProgress }: CreateOrderSupportRequestOptions = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attachments: files, ...request }: CreateOrderSupportRequestInput) => {
      if (files.length === 0) return ordersApi.createRequest(orderId, request);

      const keys = uploadedKeys ?? new Map<File, string>();
      const send = async () => {
        const attachmentKeys = await uploadAttachmentsDirect({
          orderId,
          clientRequestId: request.clientRequestId,
          files,
          uploadedKeys: keys,
          onProgress: onUploadProgress,
        });
        onUploadProgress?.(null);
        return ordersApi.createRequest(orderId, { ...request, attachmentKeys });
      };

      try {
        return await send();
      } catch (error) {
        // Anexo expirado, apagado ou alterado no S3: sobe de novo só esses e tenta uma vez mais. É o mesmo envio
        // (clientRequestId), então a segunda tentativa não duplica o ticket.
        const rejected = rejectedAttachmentIndexes(error);
        if (rejected.length === 0) throw error;
        rejected.forEach((index) => files[index] && keys.delete(files[index]));
        return send();
      } finally {
        onUploadProgress?.(null);
      }
    },
    // Entra na lista na hora: até a nova busca voltar, o mesmo tipo já fica bloqueado no menu.
    onSuccess: (created) => {
      queryClient.setQueryData<OrderSupportRequest[]>(supportRequestQueryKeys.byOrder(orderId), (requests) =>
        requests ? [created, ...requests.filter((request) => request.id !== created.id)] : requests
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: supportRequestQueryKeys.byOrder(orderId) });
    },
  });
};
